import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useBudgetGetDistributorGroupInfo } from '@/hooks/useBudgetGetDistributorGroupInfo';
import { useDistributorGetDistributorGroupInfo } from '@/hooks/useDistributorGetDistributorGroupInfo';
import { GroupType } from '@/shared/types/groupType';

export interface DistributorConflict {
  hasConflict: boolean;
  conflictGroupId?: number;
  conflictGroupName?: string;
  conflictGroupType?: GroupType;
}

interface UseDistributorConflictDetectionReturn {
  checkDistributorConflict: (userId: string, targetGroupId: number) => Promise<DistributorConflict>;
}

export function useDistributorConflictDetection(): UseDistributorConflictDetectionReturn {
  const { organizationId } = useOrganizationId();
  const { getDistributorGroupInfo: getDistributorGroupDistributorInfo } = useDistributorGetDistributorGroupInfo();
  const { getDistributorGroupInfo: getBudgetDistributorGroupInfo } = useBudgetGetDistributorGroupInfo();

  const checkDistributorConflict = async (userId: string, targetGroupId: number): Promise<DistributorConflict> => {
    try {
      const budgetDistributorInfo = await getBudgetDistributorGroupInfo(organizationId, userId);

      if (budgetDistributorInfo) {
        return {
          hasConflict: true,
          conflictGroupId: budgetDistributorInfo.id,
          conflictGroupName: budgetDistributorInfo.name || `Budget Group ${budgetDistributorInfo.id}`,
          conflictGroupType: 'budget',
        };
      }

      const distributorInfo = await getDistributorGroupDistributorInfo(organizationId, userId);

      if (distributorInfo && distributorInfo.systemId !== targetGroupId) {
        return {
          hasConflict: true,
          conflictGroupId: distributorInfo.systemId,
          conflictGroupName: distributorInfo.distributorName || `Distributor Group ${distributorInfo.systemId}`,
          conflictGroupType: 'distributor',
        };
      }

      return { hasConflict: false };
    } catch (err) {
      console.error('Error checking distributor conflict:', err);
      return { hasConflict: false };
    }
  };

  return {
    checkDistributorConflict,
  };
}
